import './tasbih-ui.css';

type TasbihInteractionMode = 'tap' | 'swipe';

type TasbihAssetConfig = {
  beadUrl?: string;
  accentBeadUrl?: string;
  tasselUrl?: string;
};

const MODE_STORAGE_KEY = '4eternity.tasbih.interaction-mode.v1';
const SWIPE_THRESHOLD_PX = 54;
const MAX_DRAG_PX = 92;
const COMMIT_ANIMATION_MS = 130;

// Später können hier echte freigestellte Tasbih-Assets hinterlegt werden.
// Solange die URLs leer sind, verwendet die Oberfläche reine CSS-Fallbacks.
const TASBIH_ASSETS: TasbihAssetConfig = {
  beadUrl: '',
  accentBeadUrl: '',
  tasselUrl: '',
};

function loadMode(): TasbihInteractionMode {
  try {
    return localStorage.getItem(MODE_STORAGE_KEY) === 'swipe' ? 'swipe' : 'tap';
  } catch {
    return 'tap';
  }
}

function saveMode(mode: TasbihInteractionMode): void {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Der Modus bleibt für die aktuelle Ansicht aktiv, auch wenn Storage blockiert ist.
  }
}

function assetCssValue(url: string | undefined): string | null {
  const trimmed = url?.trim();
  return trimmed ? `url(${JSON.stringify(trimmed)})` : null;
}

function applyConfiguredAssets(stage: HTMLElement): void {
  const bead = assetCssValue(TASBIH_ASSETS.beadUrl);
  const accent = assetCssValue(TASBIH_ASSETS.accentBeadUrl);
  const tassel = assetCssValue(TASBIH_ASSETS.tasselUrl);

  if (bead) stage.style.setProperty('--tasbih-bead-image', bead);
  if (accent) stage.style.setProperty('--tasbih-accent-bead-image', accent);
  if (tassel) stage.style.setProperty('--tasbih-tassel-image', tassel);
}

function createModePanel(): HTMLElement {
  const panel = document.createElement('div');
  panel.className = 'tasbih-mode-panel';
  panel.innerHTML = `
    <div class="tasbih-mode-copy">
      <span>Bedienung</span>
      <small data-tasbih-mode-description>1 Berührung = 1 Zählung</small>
    </div>
    <div class="tasbih-mode-switch" role="group" aria-label="Tasbih Bedienmodus">
      <button type="button" data-tasbih-mode="tap" aria-pressed="false">Tippen</button>
      <button type="button" data-tasbih-mode="swipe" aria-pressed="false">Ziehen</button>
    </div>
  `;
  return panel;
}

function createSwipeStage(): HTMLElement {
  const stage = document.createElement('div');
  stage.className = 'tasbih-swipe-stage';
  stage.tabIndex = 0;
  stage.setAttribute('role', 'button');
  stage.setAttribute('aria-label', 'Eine Tasbih-Perle ziehen, um den Zähler zu erhöhen');

  const beads = Array.from({ length: 9 }, (_, index) => {
    const accentClass = index === 7 ? ' accent' : '';
    return `<span class="tasbih-pull-bead${accentClass}" aria-hidden="true"></span>`;
  }).join('');

  stage.innerHTML = `
    <div class="tasbih-pull-viewport" aria-hidden="true">
      <div class="tasbih-pull-track" data-tasbih-pull-track>
        <span class="tasbih-pull-string"></span>
        ${beads}
        <span class="tasbih-pull-tassel"><i></i><i></i><i></i></span>
      </div>
    </div>
    <div class="tasbih-swipe-copy">
      <strong>Eine Perle ziehen</strong>
      <small>Nach oben oder unten ziehen und loslassen</small>
    </div>
    <div class="tasbih-swipe-progress" aria-hidden="true"><i></i></div>
  `;

  applyConfiguredAssets(stage);
  return stage;
}

function bindSwipeGesture(stage: HTMLElement, counter: HTMLButtonElement): void {
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let dragY = 0;
  let axis: 'vertical' | 'horizontal' | null = null;

  const setDrag = (value: number): void => {
    dragY = Math.max(-MAX_DRAG_PX, Math.min(MAX_DRAG_PX, value));
    const progress = Math.min(1, Math.abs(dragY) / SWIPE_THRESHOLD_PX);
    stage.style.setProperty('--tasbih-drag-y', `${dragY}px`);
    stage.style.setProperty('--tasbih-drag-progress', String(progress));
    stage.classList.toggle('is-ready', progress >= 1);
  };

  const reset = (): void => {
    stage.classList.remove('is-dragging', 'is-ready', 'is-committing');
    stage.style.setProperty('--tasbih-drag-y', '0px');
    stage.style.setProperty('--tasbih-drag-progress', '0');
    dragY = 0;
    axis = null;
  };

  const commitCount = (direction: number): void => {
    stage.classList.remove('is-dragging');
    stage.classList.add('is-committing', 'is-ready');
    stage.style.setProperty('--tasbih-drag-y', `${direction * MAX_DRAG_PX}px`);
    stage.style.setProperty('--tasbih-drag-progress', '1');

    window.setTimeout(() => {
      if (stage.isConnected && counter.isConnected) counter.click();
    }, COMMIT_ANIMATION_MS);
  };

  const finishPointer = (event: PointerEvent, cancelled: boolean): void => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    const completed = !cancelled && axis !== 'horizontal' && Math.abs(dragY) >= SWIPE_THRESHOLD_PX;
    const direction = dragY < 0 ? -1 : 1;
    pointerId = null;

    if (completed) {
      commitCount(direction);
    } else {
      reset();
    }
  };

  stage.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    dragY = 0;
    axis = null;
    stage.classList.remove('is-committing', 'is-ready');
    stage.classList.add('is-dragging');
    stage.setPointerCapture(event.pointerId);
    setDrag(0);
  });

  stage.addEventListener('pointermove', (event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;

    if (axis === null && Math.hypot(deltaX, deltaY) >= 8) {
      axis = Math.abs(deltaY) >= Math.abs(deltaX) ? 'vertical' : 'horizontal';
    }

    if (axis === 'horizontal') return;
    event.preventDefault();
    setDrag(deltaY);
  });

  stage.addEventListener('pointerup', (event) => finishPointer(event, false));
  stage.addEventListener('pointercancel', (event) => finishPointer(event, true));

  stage.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    counter.click();
  });
}

function enhanceTasbih(): void {
  const card = document.querySelector<HTMLElement>('.tasbih-card');
  if (!card || card.dataset.interactionEnhanced === 'true') return;

  const word = card.querySelector<HTMLElement>('.tasbih-word');
  const counter = card.querySelector<HTMLButtonElement>('[data-action="tasbih-count"]');
  const beads = card.querySelector<HTMLElement>('.beads');
  if (!word || !counter || !beads) return;

  card.dataset.interactionEnhanced = 'true';
  counter.classList.add('tasbih-tap-counter');
  beads.classList.add('tasbih-progress-beads');

  const modePanel = createModePanel();
  word.insertAdjacentElement('afterend', modePanel);

  const swipeStage = createSwipeStage();
  counter.insertAdjacentElement('afterend', swipeStage);
  bindSwipeGesture(swipeStage, counter);

  const description = modePanel.querySelector<HTMLElement>('[data-tasbih-mode-description]');
  const modeButtons = Array.from(modePanel.querySelectorAll<HTMLButtonElement>('[data-tasbih-mode]'));

  const applyMode = (mode: TasbihInteractionMode): void => {
    card.dataset.tasbihMode = mode;
    if (description) {
      description.textContent = mode === 'tap'
        ? '1 Berührung = 1 Zählung'
        : '1 vollständiger Zug = 1 Zählung';
    }

    for (const button of modeButtons) {
      const active = button.dataset.tasbihMode === mode;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', String(active));
    }
  };

  for (const button of modeButtons) {
    button.addEventListener('click', () => {
      const mode = button.dataset.tasbihMode === 'swipe' ? 'swipe' : 'tap';
      saveMode(mode);
      applyMode(mode);
    });
  }

  applyMode(loadMode());
}

let enhanceScheduled = false;
function scheduleEnhance(): void {
  if (enhanceScheduled) return;
  enhanceScheduled = true;
  queueMicrotask(() => {
    enhanceScheduled = false;
    enhanceTasbih();
  });
}

const appRoot = document.querySelector<HTMLElement>('#app');
if (appRoot) {
  const observer = new MutationObserver(scheduleEnhance);
  observer.observe(appRoot, { childList: true, subtree: true });
}

scheduleEnhance();
